import { useMemo } from 'react'
import { useUserInfo } from '@/hooks'

/**
 * 统一币种信息
 */
export interface CurrencyItem {
  /** 币种标识（小写，如 usdt） */
  key: string
  /** 币种名称（大写，如 USDT） */
  name: string
  /** 币种符号（大写，如 USDT） */
  symbol: string
  /** 币种描述 */
  description: string
  /** 图标路径 */
  icon: string
  /** 用户余额 */
  balance: string
}

/**
 * useCurrencyList 配置选项
 */
export interface UseCurrencyListOptions {
  /** 
   * 需要动态添加的额外币种列表
   * 由调用方传入完整的币种信息对象
   */
  extraCurrencies?: CurrencyItem[]
}

/**
 * 默认支持的币种基础配置
 */
const DEFAULT_CURRENCIES = [
  {
    key: 'usdt',
    name: 'USDT',
    symbol: 'USDT',
    description: 'Tether USD',
    icon: '/images/home/usdt.png'
  },
  {
    key: 'usdc',
    name: 'USDC',
    symbol: 'USDC',
    description: 'USD Coin',
    icon: '/images/home/usdc.png'
  },
  {
    key: 'usd1',
    name: 'USD1',
    symbol: 'USD1',
    description: 'USD1 Stablecoin',
    icon: '/images/home/usd1.png'
  },
  {
    key: 'usd',
    name: 'USD',
    symbol: 'USD',
    description: 'US Dollar',
    icon: '/images/home/usd.png'
  }
]

/**
 * 币种列表 Hook
 * 统一管理应用中的币种列表数据
 * 
 * @param options 配置选项，可传入需要动态添加的额外币种
 * 
 * @example
 * // 基础用法
 * const { currencyList } = useCurrencyList()
 * 
 * @example
 * // 动态扩展币种（如 AP 分支的开卡页面）
 * const { currencyList } = useCurrencyList({
 *   extraCurrencies: [{
 *     key: 'hp',
 *     name: 'HP',
 *     symbol: 'HP',
 *     description: 'HP Token',
 *     icon: '/images/home/hp.png',
 *     balance: hpBalance
 *   }]
 * })
 */
export function useCurrencyList(options: UseCurrencyListOptions = {}) {
  const { extraCurrencies = [] } = options
  const { userInfo } = useUserInfo()

  /**
   * 从 userInfo 获取默认币种余额
   */
  const getDefaultBalance = (key: string): string => {
    if (!userInfo) return '0'
    const balanceKey = key.toLowerCase() as keyof typeof userInfo
    const balance = userInfo[balanceKey]
    // 处理 number、string 类型，以及 undefined/null
    if (balance === undefined || balance === null) return '0'
    if (typeof balance === 'number') return String(balance)
    if (typeof balance === 'string') return balance || '0'
    return '0'
  }

  /**
   * 统一币种列表（包含所有信息：name, symbol, description, icon, balance）
   */
  const currencyList = useMemo<CurrencyItem[]>(() => {
    // 1. 处理默认币种
    const defaultList = DEFAULT_CURRENCIES.map(config => ({
      ...config,
      balance: getDefaultBalance(config.key)
    }))

    // 2. 合并额外币种
    return [...defaultList, ...extraCurrencies]
  }, [userInfo, extraCurrencies])

  /**
   * 获取币种余额（支持默认币种和额外币种）
   */
  const getBalance = (key: string): string => {
    const currency = currencyList.find(c => c.key.toLowerCase() === key.toLowerCase())
    return currency?.balance || '0'
  }

  /**
   * 根据 key 获取币种
   */
  const getCurrencyByKey = (key: string): CurrencyItem | undefined => {
    return currencyList.find(c => c.key.toLowerCase() === key.toLowerCase())
  }

  /**
   * 根据 name/symbol 获取币种
   */
  const getCurrencyByName = (name: string): CurrencyItem | undefined => {
    return currencyList.find(c => c.name.toLowerCase() === name.toLowerCase() || c.symbol.toLowerCase() === name.toLowerCase())
  }

  return {
    /** 统一币种列表 */
    currencyList,
    /** 根据 key 获取币种 */
    getCurrencyByKey,
    /** 根据 name/symbol 获取币种 */
    getCurrencyByName,
    /** 获取币种余额 */
    getBalance
  }
}

// 兼容旧代码的类型别名
export type CoinInfo = CurrencyItem
export type CurrencyConfig = CurrencyItem
